import { Input, LightMode, Text } from "@repo/skapa";
import { isElement } from "@repo/utils";
import isString from "lodash/isString";
import { type ReactElement, type ReactNode, useState } from "react";

import { pixels } from "../Variables";
import { FlexDiv } from "./FlexDiv";
import { Label } from "./Label";

interface LabelFieldProps {
  id: string;
  label: string;
  value?: string | ReactElement;
  editable?: boolean;
  onChange?: (newValue?: string) => void;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  prefix?: string;
  autoFocus?: boolean;
  noWrap?: boolean;
}

export const LabelField = ({ value, onChange, ...props }: LabelFieldProps) => {
  const [textValue, setTextValue] = useState(value);

  return (
    <StatelessLabelField
      {...props}
      value={textValue}
      onChange={isElement(value) ? undefined : setTextValue}
      onBlur={() => {
        if (isString(textValue) && value !== textValue) {
          onChange?.(textValue);
        }
      }}
    />
  );
};

interface StatelessLabelFieldProps extends LabelFieldProps {
  onBlur?: (newValue?: string) => void;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  autoFocus?: boolean;
}

export const StatelessLabelField = ({
  id,
  label,
  value,
  editable,
  onChange,
  onKeyUp,
  prefix,
  onBlur,
  autoFocus,
  noWrap,
}: StatelessLabelFieldProps) => {
  return (
    <>
      {editable && !isElement(value) ? (
        <Input
          id={id}
          type="text"
          label={label}
          value={value ?? ""}
          autoFocus={autoFocus}
          onChange={(e) => onChange?.(e.target.value)}
          prefixLabel={prefix}
          onBlur={(e) => onBlur?.(e.target.value)}
          onKeyUp={onKeyUp}
        />
      ) : (
        <div>
          <Label label={label} bold noWrap={noWrap} />
          {isElement(value) ? value : <Text noWrap={noWrap}>{value}</Text>}
        </div>
      )}
    </>
  );
};

interface SimpleLabelFieldProps {
  label: string;
  value: string | ReactNode | undefined;
  bold?: boolean;
  small?: boolean;
  color?: string;
  noWrap?: boolean;
}
export const SimpleLabelField = ({
  label,
  value,
  small,
  color,
  noWrap,
  bold,
}: SimpleLabelFieldProps) => (
  <FlexDiv gap={pixels.space8}>
    <Text textStyle={small ? "Body.S" : "Body.M"} noWrap={noWrap} bold={bold}>
      {label}:
    </Text>
    <Text
      textStyle={small ? "Body.S" : "Body.M"}
      color={color ?? LightMode.colourNeutral5}
      aria-label={label}
      noWrap={noWrap}
    >
      {value}
    </Text>
  </FlexDiv>
);

export const InlineLabelField = ({
  label,
  value,
  bold,
}: SimpleLabelFieldProps) => (
  <FlexDiv justifyContent="space-between" grow>
    {bold ? (
      <>
        <Text textStyle="Heading.XS">{label}:</Text>
        <Text textStyle="Heading.XS">{value}</Text>
      </>
    ) : (
      <>
        <Text>{label}</Text>
        <Text>{value}</Text>
      </>
    )}
  </FlexDiv>
);
